import { EmailMessageListItem, EmailMessageThreadItem } from "../../api/clients.api";
import { inject } from "react-ioc";
import { BaseListStore } from "app/modules/common/stores/BaseListStore";
import { ApiClients } from "app/modules/company/services/AxiosBaseClient";
import { FieldState, FormState } from "formstate";
import { action, makeObservable, observable, runInAction } from "mobx";
import { ApiErrorHandler } from "app/modules/common/static/ErrrorHandler";
import { createRef } from "react";
import { required } from "app/modules/common/form/validators";
import { DialogStore } from "app/modules/common/dialog/DialogStore";
import { DialogAIExtraction } from "./dialog.ai.extraction";
import React from "react";

export class InboxStore extends BaseListStore<EmailMessageListItem> {
    @inject companyApiClients: ApiClients;
    @inject dialogStore: DialogStore;
    public editorRef = createRef<any>();

    emailThread: EmailMessageThreadItem[] = [];
    totalCount: number = 0;

    selectedMessage: EmailMessageListItem | null = null;
    selectedMessageThread: EmailMessageThreadItem | null = null;
    isLeftPanelHidden: boolean = false;
    noIntegrations: boolean = false;
    isMessageLoading: boolean = false;
    isReplyLoading: boolean = false;

    replyToEmails: string[] = [];
    replyCcEmails: string[] = [];
    replyToInput: string = '';
    replyCcInput: string = '';
    attachments: File[] = [];

    replyForm = new FormState({
        content: new FieldState("").validators(required)
    });

    filters = new FormState({
        integrationId: new FieldState<number>(null),
        from: new FieldState<string>(''),
        subject: new FieldState<string>(''),
        search: new FieldState<string>(''),
        receivedDateFrom: new FieldState<moment.Moment>(null),
        receivedDateTo: new FieldState<moment.Moment>(null),
        pageSize: new FieldState<number>(50),
        offset: new FieldState<number>(0),
        showUnreadOnly: new FieldState<boolean>(false)
    });

    constructor() {
        super();

        makeObservable(this, {
            editorRef: observable,
            emailThread: observable,
            isLeftPanelHidden: observable,
            selectedMessage: observable,
            selectedMessageThread: observable,
            isMessageLoading: observable,
            isReplyLoading: observable,
            totalCount: observable,
            replyToEmails: observable,
            replyCcEmails: observable,
            replyToInput: observable,
            replyCcInput: observable,
            replyForm: observable,
            filters: observable,
            attachments: observable,
            items: observable,
            clearEditor: action,
            selectMessage: action,
            selectMessageThread: action,
            sendReply: action,
            markAsRead: action,
            toggleUnreadFilter: action,
            setReplyToEmails: action,
            setReplyCcEmails: action,
            setReplyToInput: action,
            setReplyCcInput: action,
            addReplyToEmail: action,
            addReplyCcEmail: action,
            addAttachment: action,
            removeAttachment: action,
            updateNewMessages: action,
            goToPreviousPage: action,
            goToNextPage: action,
            openAIExtractionDialog: action
        });
    }

    async loadData() {
        await this.wrapLoadingApiCall(async () => {
            await this.LoadEmails();
        });
    }

    private async LoadEmails() {
        const response = await this.companyApiClients.emailInboxClient.getEmailMessages(
            this.filters.$.integrationId.value,
            this.filters.$.search.value,
            this.filters.$.from.value,
            this.filters.$.subject.value,
            this.filters.$.receivedDateFrom.value,
            this.filters.$.receivedDateTo.value,
            this.filters.$.showUnreadOnly.value,
            this.filters.$.pageSize.value,
            this.filters.$.offset.value
        );

        runInAction(() => {
            this.items = response.items;
            this.totalCount = response.count ?? 0;
        });
    }

    async applySearchFilter() {
        this.filters.$.offset.onChange(0);
        this.selectedMessage = null;
        this.selectedMessageThread = null;
        this.emailThread.length = 0;

        await this.loadData();

        if(this.items.length > 0) {
            await this.selectMessage(this.items[0]);
        }
    }

    async clearFilters() {
        this.filters.$.from.onChange('');
        this.filters.$.subject.onChange('');
        this.filters.$.search.onChange('');
        this.filters.$.receivedDateFrom.onChange(null);
        this.filters.$.receivedDateTo.onChange(null);
        this.filters.$.offset.onChange(0);

        await this.loadData();
    }

    async selectMessage(message: EmailMessageListItem) {
        if (!message.isRead) {
            await this.markAsRead(message.id);
        }

        if(message.id == this.selectedMessage?.id) {
            return;
        }

        this.selectedMessage = message;

        try{
            this.isMessageLoading = true;

            const response = await this.companyApiClients.emailInboxClient.getEmailThread(message.id);
            this.emailThread = response;

            this.selectMessageThread(response[0]);
        }
        catch (ex) { ApiErrorHandler(ex); }
        finally {
            runInAction(() => {
                this.isMessageLoading = false;
            })
        }
    }

    async updateThreadMessages() {
         try{
            const response = await this.companyApiClients.emailInboxClient.getEmailThread(this.selectedMessageThread.id);
            this.emailThread = response;
        }
        catch (ex) { ApiErrorHandler(ex); }
    }

    async selectMessageThread(thread: EmailMessageThreadItem) {
        if (!thread.isRead) {
            await this.markAsRead(thread.id);
        }

        this.selectedMessageThread = thread;
        this.clearEditor();
        this.replyToEmails = thread.from.map(x => x.address);
        this.replyCcEmails = thread.cc.map(x => x.address);
    }

    async clearEditor() {
        this.replyForm.$.content.onChange('');
        this.replyToEmails = [];
        this.replyCcEmails = [];
        this.replyToInput = '';
        this.replyCcInput = '';
        this.attachments = [];
        this.filters.$.offset.onChange(0);

        if (this.editorRef.current) {
            this.editorRef.current?.setContent('');
        }
    }

    async goToPreviousPage() {
        const newOffset = this.filters.$.offset.value - this.filters.$.pageSize.value;
        this.filters.$.offset.onChange(Math.max(0, newOffset));
        await this.loadData();
    }

    async goToNextPage() {
        const newOffset = this.filters.$.offset.value + this.filters.$.pageSize.value;
        this.filters.$.offset.onChange(newOffset);
        await this.loadData();
    }

    async Init(integrationId: number) {
        await this.wrapInitLoadingApiCall(async () => {
            this.filters.$.integrationId.onChange(integrationId);
            await this.loadData();
            if(this.items.length > 0) {
                await this.selectMessage(this.items[0]);
            }
        });
    }

    applySpecificFilters() {}

    async sendReply() {
        if (!this.selectedMessageThread) return;
        this.isReplyLoading = true;
        try {
            await this.companyApiClients.emailInboxClient.replyToEmail(
                this.selectedMessageThread.id,
                this.replyForm.$.content.value,
                this.replyToEmails,
                this.replyCcEmails,
                this.attachments.map(file => ({ data: file, fileName: file.name }))
            );
            this.replyForm.$.content.onChange("");
            this.attachments = [];
            await this.loadData();
        } finally {
            this.isReplyLoading = false;
        }
    }

    async markAsRead(messageId: number) {
        try {
            await this.companyApiClients.emailInboxClient.markAsRead(messageId);
            runInAction(() => {
                // Update the message in the items list
                const message = this.items.find(m => m.id === messageId);
                if (message) {
                    message.isRead = true;
                }
                // Update the message in the thread if it exists
                const threadMessage = this.emailThread.find(m => m.id === messageId);
                if (threadMessage) {
                    threadMessage.isRead = true;
                }
                // Update the selected message if it matches
                if (this.selectedMessageThread?.id === messageId) {
                    this.selectedMessageThread.isRead = true;
                }
                // Force a state update by creating new arrays
                this.items = [...this.items];
                this.emailThread = [...this.emailThread];
            });
        } catch (ex) {
            ApiErrorHandler(ex);
        }
    }

    async updateNewMessages() {
        await this.loadData();
        await this.updateThreadMessages();
    }

    async toggleUnreadFilter() {
        this.filters.$.showUnreadOnly.onChange(!this.filters.$.showUnreadOnly.value);
        this.filters.$.offset.onChange(0); // Reset to first page when changing filter
        await this.loadData();
    }

    setReplyToEmails = (emails: string[]) => {
        this.replyToEmails = emails;
    }

    setReplyCcEmails = (emails: string[]) => {
        this.replyCcEmails = emails;
    }

    setReplyToInput = (value: string) => {
        this.replyToInput = value;
    }

    setReplyCcInput = (value: string) => {
        this.replyCcInput = value;
    }

    setAttachments = (files: File[]) => {
        this.attachments = files;
    }

    addReplyToEmail = (email: string) => {
        if (!this.replyToEmails.includes(email)) {
            this.replyToEmails = [...this.replyToEmails, email];
        }
    }

    addReplyCcEmail = (email: string) => {
        if (!this.replyCcEmails.includes(email)) {
            this.replyCcEmails = [...this.replyCcEmails, email];
        }
    }

    addAttachment = (file: File) => {
        this.attachments = [...this.attachments, file];
    }

    removeAttachment = (index: number) => {
        const newAttachments = [...this.attachments];
        newAttachments.splice(index, 1);
        this.attachments = newAttachments;
    }

    openAIExtractionDialog = (emailId: number, integrationId: number) => {
        const key = "ai_extraction_" + emailId;
        this.dialogStore.addDialog({
            key: key,
            component: React.createElement(DialogAIExtraction, {
                emailId: emailId,
                integrationId: integrationId,
                closeModal: () => this.dialogStore.closeDialog(key)
            })
        });
    }
}
